(() => {
  const sourceSelector = '[role="dialog"][aria-modal="true"], [data-fp-modal-a11y]';
  const ownerSelector = 'dialog, details, modal-dialog, quick-add-modal, product-modal, pickup-availability-drawer, cart-drawer, #cart-notification';
  const managedDialogs = new Set();
  const ownerObservers = new WeakMap();
  const openState = new WeakMap();
  const openers = new WeakMap();
  const pendingSync = new Set();
  let pendingOpener = null;
  let openerTimer = 0;
  let frame = 0;

  const ownerFor = (dialog) => dialog.closest(ownerSelector) || dialog;

  const isOpen = (dialog, owner = ownerFor(dialog)) => {
    if (owner.matches('dialog')) return owner.open;
    if (owner.matches('details')) return owner.open;
    if (owner.matches('modal-dialog, quick-add-modal, product-modal, pickup-availability-drawer')) {
      return owner.hasAttribute('open');
    }
    if (owner.matches('cart-drawer')) return owner.classList.contains('active');
    if (owner.matches('#cart-notification')) return owner.classList.contains('active');
    if (dialog.hidden || owner.hidden || dialog.getAttribute('aria-hidden') === 'true') return false;
    return owner.style.display !== 'none' && owner.style.visibility !== 'hidden';
  };

  const syncDialog = (dialog) => {
    if (!dialog.isConnected) return;
    const open = isOpen(dialog);
    const wasOpen = openState.get(dialog) === true;
    if (open) {
      dialog.setAttribute('aria-modal', 'true');
      if (!wasOpen && pendingOpener?.isConnected && !dialog.contains(pendingOpener)) {
        openers.set(dialog, pendingOpener);
        pendingOpener = null;
        window.clearTimeout(openerTimer);
      }
    } else {
      dialog.removeAttribute('aria-modal');
      if (wasOpen) {
        const active = document.activeElement;
        const focusWasLost = !active || active === document.body || dialog.contains(active);
        const opener = openers.get(dialog);
        if (focusWasLost && opener?.isConnected) opener.focus({ preventScroll: true });
      }
    }
    openState.set(dialog, open);
  };

  const flush = () => {
    frame = 0;
    pendingSync.forEach(syncDialog);
    pendingSync.clear();
  };

  const scheduleDialog = (dialog) => {
    if (!dialog?.isConnected) return;
    pendingSync.add(dialog);
    if (!frame) frame = window.requestAnimationFrame(flush);
  };

  const unwatch = (dialog) => {
    ownerObservers.get(dialog)?.disconnect();
    ownerObservers.delete(dialog);
    pendingSync.delete(dialog);
    managedDialogs.delete(dialog);
  };

  const watch = (dialog) => {
    if (!(dialog instanceof HTMLElement) || managedDialogs.has(dialog)) return;
    const owner = ownerFor(dialog);
    const observer = new MutationObserver(() => scheduleDialog(dialog));
    observer.observe(owner, {
      attributes: true,
      attributeFilter: ['class', 'hidden', 'open', 'aria-hidden', 'style']
    });
    managedDialogs.add(dialog);
    ownerObservers.set(dialog, observer);
    syncDialog(dialog);
  };

  const watchWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;
    if (scope instanceof Element && scope.matches(sourceSelector)) watch(scope);
    scope.querySelectorAll(sourceSelector).forEach(watch);
  };

  const syncHeaderDrawers = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;
    const drawers = [];
    if (scope instanceof Element && scope.matches('header-drawer')) drawers.push(scope);
    scope.querySelectorAll('header-drawer').forEach((drawer) => drawers.push(drawer));
    drawers.forEach((drawer) => {
      drawer.header = drawer.header || drawer.closest('.section-header') || document.querySelector('.section-header');
    });
  };

  const unwatchWithin = (scope) => {
    managedDialogs.forEach((dialog) => {
      if (!dialog.isConnected || scope === dialog || scope?.contains?.(dialog)) unwatch(dialog);
    });
  };

  const rememberOpener = (element) => {
    if (!(element instanceof HTMLElement)) return;
    pendingOpener = element;
    window.clearTimeout(openerTimer);
    openerTimer = window.setTimeout(() => { pendingOpener = null; }, 10000);
  };

  watchWithin(document);
  syncHeaderDrawers(document);
  document.addEventListener('click', (event) => {
    rememberOpener(event.target.closest?.('[aria-haspopup="dialog"], summary, button, [role="button"]'));
  }, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') rememberOpener(document.activeElement);
  }, true);

  const discoveryObserver = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) {
        watchWithin(node);
        syncHeaderDrawers(node);
      }
    }));
    managedDialogs.forEach((dialog) => {
      if (!dialog.isConnected) unwatch(dialog);
    });
  });
  discoveryObserver.observe(document.body || document.documentElement, { subtree: true, childList: true });

  document.addEventListener('shopify:section:load', (event) => {
    watchWithin(event.target);
    syncHeaderDrawers(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => unwatchWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => watchWithin(event.target));
})();
